import { Injectable } from '@angular/core';
import { Response } from '@angular/http';
import { Router } from '@angular/router';
import { HttpClient, HttpResponse } from '@angular/common/http';

import { Observable } from 'rxjs';
import { PaginatedData } from '../module/acs-support/acs-support';
import { LooseObject } from '../objects/loose-object';
import { GeneratedJob } from '../module/generate-recurring-jobs/view/view.component';
//Get the base url from the environment file.
import { environment } from '../../environments/environment';
import { Config } from 'protractor';
import { RepeatedEvery } from '../module/generate-recurring-jobs/view/shared/objects/repeated-every';
import { DateFilter } from '../module/timesheets/classes/shared-types';
import { filled, fallback, data_get, data_is_true } from '../shared/utils/common';
import { TranslateService } from '@ngx-translate/core';
import { ApiService } from './api.service';

const kBaseUrl: string = environment.url + "/recurring_jobs/";

@Injectable()
export class RecurringJobsService {

  private weekTranslation = '';
  private weeksTranslation = '';
  private monthTranslation = '';
  private monthsTranslation = '';
  private everyTranslation = '';

  constructor (
    private http: HttpClient,
    private router: Router,
    private translateService: TranslateService,
		private readonly _api: ApiService,
  ) {
    this.weekTranslation = this.translateService.instant('week');
    this.weeksTranslation = this.translateService.instant('weeks');
    this.monthTranslation = this.translateService.instant('month');
    this.monthsTranslation = this.translateService.instant('months');
    this.everyTranslation = this.translateService.instant('every');
  }

  /**
   * save record
   *
   * @param strModule - request module
   * @param arData - record data
   * @param strId - default null, for update record
   */
  saveRecord(strModule, arData, strId = null): Observable<HttpResponse<Config>> {
    // Create request parameters
    let body = new URLSearchParams();
    // Append post parameter
    body.append('module', strModule);
    body.append('data', JSON.stringify(arData));
    if (strId) body.append('id', strId);
    // Post request
    return this.http.post<Response>(kBaseUrl+ 'save', body.toString(), { observe: 'response' });
  }

  /**
   * format reapeated every field
   *
   * @param data
   * @returns
   */
  formatRepeatedEvery(data: string): string {
    if (filled(data)) {
      const [num, unit] = data.split(' ');
      if (filled(num) && filled(unit)) {
        let parsedNumber = parseInt(num);
        let newUnit;
        if (parsedNumber % 7 === 0 && unit == 'days') {
          newUnit = (parsedNumber / 7 == 1) ? this.weekTranslation : this.weeksTranslation;
        } else {
          if (unit.includes('mon')) {
            newUnit = unit == 'mon' ? this.monthTranslation : this.monthsTranslation;
          } else {
            newUnit = unit.charAt(0).toUpperCase() + unit.slice(1);
          }
        }
        let newNum = (parsedNumber % 7 == 0 && unit == 'days') ? parsedNumber / 7 : parsedNumber;
        let repeatedEvery = `${this.everyTranslation} ${newNum} ${newUnit}`;
        return !data.includes('Every') ? repeatedEvery : data;
      }
    }
  }

  getExistingRepeatedEveryValues(): Observable<RepeatedEvery[]> {
    return this._api.callPrivate$({
      path:'/recurring_jobs/existing_repeated_every_values',
      action: 'post',
      onSuccessResponse: (response) => {
        const arData = [];

        response.forEach(value => {
          arData.push(new RepeatedEvery(
            data_get(value, 'duration'),
            data_get(value, 'interval')
          ));
        });

        return arData;
      },
    })
  }

  combinedListForWidget(args: {
    current_page: number,
    recurring_job_id: string,
    job_status?: string,
  }): Observable<PaginatedData> {
		return this._api.callPrivate$<PaginatedData>({
			path: `/recurring_jobs/combined_list?page=${args.current_page}`,
			action: 'post',
			body: {
        recurring_job_id: args.recurring_job_id,
				... (filled(args.job_status) && {
					'job_status': args.job_status,
				}),
			},
      onSuccessResponse: (response) => {
        return fallback(
          response.get('data'),
          {
            fallback: () => {
              return {};
            }
          }
        ) as PaginatedData;
      },
		});
  }

  listRecurringJobs(args: {
    current_page: number,
    page_size?: number,
    repeated_every?: string,
    user_id?: string,
    customer_id?: string,
    site_id?: string,
    asset_type_id?: string,
    service_group_id?: string,
    next_job_creation_date?: DateFilter,
  }): Observable<PaginatedData> {
		return this._api.callPrivate$<PaginatedData>({
			path: `/recurring_jobs/scheduled?page=${args.current_page}`,
			action: 'post',
			body: {
				... (filled(args.repeated_every) && {
					'repeated_every': args.repeated_every,
				}),
				... (filled(args.user_id) && {
					'user_id': args.user_id,
				}),
				... (filled(args.customer_id) && {
					'customer_id': args.customer_id,
				}),
				... (filled(args.site_id) && {
					'site_id': args.site_id,
				}),
				... (filled(args.asset_type_id) && {
					'asset_type_id': args.asset_type_id,
				}),
        ... (filled(args.service_group_id) && {
          'service_group_id': args.service_group_id,
        }),
				... ((filled(args.next_job_creation_date.from) || filled(args.next_job_creation_date.to)) && {
					'next_job_date': args.next_job_creation_date,
				}),
        page_size: fallback(args.page_size, {
          fallback: () => 50,
        }),
			},
      onSuccessResponse: (response) => {
        return fallback(
          response.get('data'),
          {
            fallback: () => {
              return {};
            }
          }
        ) as PaginatedData;
      },
		});
  }

  generateGroupedRecurringJobs(args: {
    recurring_job_ids_with_service_groups: string[],
    recurring_job_ids_without_service_groups: string[],
    selected_all: boolean,
    repeated_every?: string,
    user_id?: string,
    customer_id?: string,
    site_id?: string,
    asset_type_id?: string,
    service_group_id?: string,
    next_job_creation_date?: DateFilter,
  }): Observable<GeneratedJob[]> {
    return this._api.callPrivate$<GeneratedJob[]>({
			path: '/recurring_jobs/generate_grouped',
			action: 'post',
      body: {
        recurring_job_ids_with_service_groups: args.recurring_job_ids_with_service_groups,
        recurring_job_ids_without_service_groups: args.recurring_job_ids_without_service_groups,
				... (filled(args.repeated_every) && {
					'repeated_every': args.repeated_every,
				}),
				... (filled(args.user_id) && {
					'user_id': args.user_id,
				}),
				... (filled(args.customer_id) && {
					'customer_id': args.customer_id,
				}),
				... (filled(args.site_id) && {
					'site_id': args.site_id,
				}),
				... (filled(args.asset_type_id) && {
					'asset_type_id': args.asset_type_id,
				}),
        ... (filled(args.service_group_id) && {
          'service_group_id': args.service_group_id,
        }),
				... ((filled(args.next_job_creation_date.from) || filled(args.next_job_creation_date.to)) && {
					'next_job_date': args.next_job_creation_date,
				}),
      },
      onSuccessResponse: (response) => {
        return fallback(response.get('items'), {
          fallback: () => [],
        });
      },
    });
  }
}
